/**
 * Offline-first assignment store — CRUD over the `taskTeams` and `taskCrew`
 * tables introduced in database version 5.
 *
 * A task's crew is written as a whole: `saveTaskTeams()` replaces every team of
 * one task in a single transaction, so the board can never be caught holding
 * half a convoy. The crew itself is filed row by row in `taskCrew`, each row
 * stamped with the `teamId` of the vehicle it rides on — there is no flat crew
 * list, and `getTaskTeams()` rejoins the two tables on read.
 *
 * Reads are open; every mutation goes through the permissions engine and needs
 * `ASSIGN_TASK`, exactly as the fleet and task stores guard their own writes.
 * The rules that decide whether a crew is legal are not re-implemented here:
 * they live in `src/core/assignment-rules.ts` and this module calls into them.
 */
import { db } from './index';
import { recordAssignmentOverride } from './audit-store';
import { logTaskTimelineEvent } from './timeline-store';
import { assertPermission } from '../core/permissions';
import { assertTaskTeams, normalizeTeams, teamCrewMembers } from '../core/assignment-rules';
import { activeTasks } from '../core/recycle-bin';
import { sortTasks } from '../core/task-filters';
import type { ScheduleEntry } from '../core/conflict-checker';
import type { StoredTaskTeam, TaskCrewMember, TaskTeam, TaskTeamDraft } from '../types/assignment';
import { emitStoreChange } from '../core/store-events';
import { isActiveTask } from '../types/task';
import type { Task } from '../types/task';
import type { User } from '../types/user';

/** The team row without its crew — the crew lives in `taskCrew`. */
function stored(team: TaskTeam): StoredTaskTeam {
  const { passengers: _passengers, ...row } = team;
  return row;
}

/**
 * Rejoins the crew rows onto their teams, oldest team first. Teams added in
 * the same save share a stamp, so the vehicle breaks the tie and the convoy
 * always reads back in the same order.
 */
function withCrew(rows: readonly StoredTaskTeam[], crew: readonly TaskCrewMember[]): TaskTeam[] {
  return rows
    .map((row) => ({
      ...row,
      passengers: crew.filter(
        (member) => member.teamId === row.id && member.crewRole === 'passenger',
      ),
    }))
    .sort(
      (a, b) =>
        a.createdAt.localeCompare(b.createdAt) || a.vehicleId.localeCompare(b.vehicleId),
    );
}

// ── Reads ────────────────────────────────────────────────────────────

/** Every vehicle team on one task, crew included. */
export async function getTaskTeams(taskId: string): Promise<TaskTeam[]> {
  const rows = await db.taskTeams.where('taskId').equals(taskId).toArray();
  if (rows.length === 0) return [];
  const crew = await db.taskCrew
    .where('teamId')
    .anyOf(rows.map((row) => row.id))
    .toArray();
  return withCrew(rows, crew);
}

/** One team, crew included, or `null` when it is unknown. */
export async function getTaskTeam(teamId: string): Promise<TaskTeam | null> {
  const row = await db.taskTeams.get(teamId);
  if (!row) return null;
  const crew = await db.taskCrew.where('teamId').equals(teamId).toArray();
  return withCrew([row], crew)[0] ?? null;
}

/**
 * The teams of several tasks at once, crew included — what the dashboard and
 * the dispatch sheet read, so a board of thirty tasks is two queries and not
 * sixty. The rows come back in the same order {@link getTaskTeams} uses.
 */
export async function getTeamsForTasks(taskIds: readonly string[]): Promise<TaskTeam[]> {
  const ids = [...new Set(taskIds.filter(Boolean))];
  if (ids.length === 0) return [];
  const rows = await db.taskTeams.where('taskId').anyOf(ids).toArray();
  if (rows.length === 0) return [];
  const crew = await db.taskCrew
    .where('teamId')
    .anyOf(rows.map((row) => row.id))
    .toArray();
  return withCrew(rows, crew);
}

/** Every vehicle team in the database, crew included. */
export async function getAllTaskTeams(): Promise<TaskTeam[]> {
  const [rows, crew] = await Promise.all([db.taskTeams.toArray(), db.taskCrew.toArray()]);
  return withCrew(rows, crew);
}

/** Every crew row of one team — driver and escort included. */
export async function getTeamCrew(teamId: string): Promise<TaskCrewMember[]> {
  return db.taskCrew.where('teamId').equals(teamId).toArray();
}

/**
 * How many vehicle teams are bound to one vehicle. This is what the fleet
 * store asks before it lets a vehicle be removed, so a convoy is never left
 * pointing at a record that no longer exists.
 */
export async function countVehicleAssignments(vehicleId: string): Promise<number> {
  if (!vehicleId) return 0;
  return db.taskTeams.where('vehicleId').equals(vehicleId).count();
}

/**
 * How many crew rows bind one soldier, in any role and on any vehicle —
 * driver, escort or passenger. An external passenger carries no soldier id, so
 * a manually added rider never counts here.
 */
export async function countSoldierAssignments(soldierId: string): Promise<number> {
  if (!soldierId) return 0;
  return db.taskCrew.where('soldierId').equals(soldierId).count();
}

/**
 * The tasks a soldier is bound to, in any role and on any vehicle,
 * chronologically. An external passenger holds no soldier id, so a manually
 * added rider never turns up here.
 */
export async function getSoldierAssignedTasks(soldierId: string): Promise<Task[]> {
  if (!soldierId) return [];
  const crew = await db.taskCrew.where('soldierId').equals(soldierId).toArray();
  if (crew.length === 0) return [];

  const teamIds = [...new Set(crew.map((member) => member.teamId))];
  const teams = await db.taskTeams.bulkGet(teamIds);
  const taskIds = [...new Set(teams.flatMap((team) => (team ? [team.taskId] : [])))];
  if (taskIds.length === 0) return [];

  const tasks = await db.tasks.bulkGet(taskIds);
  return sortTasks(tasks.flatMap((task) => (task ? [task] : [])));
}

/**
 * Every other open task that already has a convoy on it — what the conflict
 * engine reads a time overlap out of. Closed and cancelled tasks hold nobody,
 * so they never appear; the task being crewed is excluded by id, since its own
 * teams are governed by the exclusivity rule instead.
 *
 * A deleted task holds nobody either, and that is not the same test: deletion
 * is soft, so a task in the recycle bin keeps whatever status it had when the
 * manager threw it away. Filtering by status alone left a binned task blocking
 * its vehicle and its crew — the bin is skipped here as well, and again in the
 * engine (`isBookableTask`), so neither path can reintroduce the ghost.
 */
export async function getAssignmentSchedule(excludeTaskId?: string): Promise<ScheduleEntry[]> {
  const [tasks, rows, crew] = await Promise.all([
    db.tasks.toArray(),
    db.taskTeams.toArray(),
    db.taskCrew.toArray(),
  ]);

  const entries: ScheduleEntry[] = [];
  for (const task of sortTasks(activeTasks(tasks))) {
    if (task.id === excludeTaskId) continue;
    if (!isActiveTask(task)) continue;
    const taskRows = rows.filter((row) => row.taskId === task.id);
    if (taskRows.length === 0) continue;
    entries.push({ task, teams: withCrew(taskRows, crew) });
  }
  return entries;
}

// ── Writes ───────────────────────────────────────────────────────────

/** Drops the teams and every crew row that hangs off them. */
async function dropTeams(teamIds: readonly string[]): Promise<void> {
  if (teamIds.length === 0) return;
  await db.taskCrew.where('teamId').anyOf([...teamIds]).delete();
  await db.taskTeams.bulkDelete([...teamIds]);
}

/**
 * Replaces the whole crew of one task. Every draft is normalised and the set
 * is checked as a whole before anything is written, so a save either lands a
 * legal convoy or changes nothing at all.
 *
 * A team may carry overrides — warnings the manager assigned through anyway.
 * Each one that was not already on the stored team is filed in the audit
 * ledger after the convoy lands, so the reason, the manager and the moment
 * survive every later re-save of the same team.
 */
export async function saveTaskTeams(
  actor: User,
  taskId: string,
  drafts: readonly TaskTeamDraft[],
): Promise<TaskTeam[]> {
  assertPermission(actor, 'ASSIGN_TASK');
  if (!taskId) throw new Error('Task id is required');
  if (!(await db.tasks.get(taskId))) throw new Error('Unknown task');

  const existing = await getTaskTeams(taskId);
  const teams = normalizeTeams(drafts, taskId, existing);
  assertTaskTeams(teams);

  await db.transaction('rw', db.taskTeams, db.taskCrew, async () => {
    await dropTeams(existing.map((team) => team.id));
    await db.taskTeams.bulkPut(teams.map(stored));
    await db.taskCrew.bulkPut(teams.flatMap(teamCrewMembers));
  });

  const alreadyLogged = new Set(
    existing.flatMap((team) => (team.overrides ?? []).map((override) => override.id)),
  );
  for (const team of teams) {
    for (const override of team.overrides ?? []) {
      if (alreadyLogged.has(override.id)) continue;
      await recordAssignmentOverride(actor, taskId, team.id, override);
    }
  }

  // One step on the task's feed per save, not one per vehicle: the convoy is
  // written as a whole, so it reads back as one decision.
  if (teams.length > 0) {
    const crewCount = teams.reduce((total, team) => total + teamCrewMembers(team).length, 0);
    await logTaskTimelineEvent(actor, taskId, 'teams_assigned', {
      description: `${teams.length} רכבים · ${crewCount} אנשי צוות`,
      metadata: {
        teamIds: teams.map((team) => team.id),
        vehicleIds: teams.map((team) => team.vehicleId),
      },
    });
  }

  // The convoy decides who is out, so the rosters and the dashboard counters
  // are wrong the instant this lands until they hear about it.
  emitStoreChange('assignments');
  return teams;
}

/** Removes one vehicle team from a task, crew and all. */
export async function deleteTaskTeam(actor: User, teamId: string): Promise<boolean> {
  assertPermission(actor, 'ASSIGN_TASK');
  if (!(await db.taskTeams.get(teamId))) return false;
  await db.transaction('rw', db.taskTeams, db.taskCrew, async () => {
    await dropTeams([teamId]);
  });
  emitStoreChange('assignments');
  return true;
}

/**
 * Cascade for a task that is being deleted. Called from `deleteTask()` behind
 * its own `DELETE_TASK` guard, so no crew row is ever orphaned.
 */
export async function deleteTaskTeams(taskId: string): Promise<number> {
  const rows = await db.taskTeams.where('taskId').equals(taskId).toArray();
  if (rows.length === 0) return 0;
  await db.transaction('rw', db.taskTeams, db.taskCrew, async () => {
    await dropTeams(rows.map((row) => row.id));
  });
  emitStoreChange('assignments');
  return rows.length;
}

// ── Maintenance ──────────────────────────────────────────────────────

/** Wipes both assignment tables — used by the "reset local data" action. */
export async function resetAssignmentData(): Promise<void> {
  await Promise.all([db.taskTeams.clear(), db.taskCrew.clear()]);
  emitStoreChange('assignments');
}
